// src/pages/Transactions/hooks/useTransactionHistory.js

import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { TransactionContext } from '../../../contexts/TransactionContext';
import { WalletContext } from '../../../contexts/WalletContext';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { fetchTransactions } from '../../../services/transactionFetcher';

/**
 * Hook to manage transaction history with infinite pagination
 * Loads first page initially, then appends more pages when requested
 */
const useTransactionHistory = (options = {}) => {
  const { limit = 30 } = options;
  
  // Access contexts
  const { getTransactions, addTransaction } = useContext(TransactionContext);
  const { address } = useContext(WalletContext);
  const { currentChain } = useContext(NetworkContext);
  
  // State for transaction history
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [nextToken, setNextToken] = useState(null);
  
  // Use refs to track the current chain and address
  const chainRef = useRef(currentChain?.key);
  const addressRef = useRef(address);
  
  // Load initial transactions
  useEffect(() => {
    if (!address || !currentChain) return;
    
    // Check if chain or address changed
    const chainChanged = chainRef.current !== currentChain.key;
    const addressChanged = addressRef.current !== address;
    
    // Update refs
    chainRef.current = currentChain.key;
    addressRef.current = address;
    
    // Only reload if chain or address changed
    if (!chainChanged && !addressChanged && transactions.length > 0) {
      return;
    }
    
    const loadInitialTransactions = async () => {
      setIsLoading(true);
      setError(null);
      setNextToken(null); // Reset pagination token on initial load
      
      try {
        console.log(`Loading initial transactions for ${address} on ${currentChain.key}`);
        
        // Fetch transactions directly from API
        const result = await fetchTransactions(
          currentChain.key,
          address,
          { limit }
        );
        
        console.log(`Loaded ${result.transactions.length} transactions, hasMore: ${result.hasMore}`);
        
        // Update state with fetched transactions
        setTransactions(result.transactions);
        setHasMore(result.hasMore);
        setNextToken(result.nextToken);
        
        // Silently add to storage
        if (result.transactions.length > 0) {
          console.log(`[useTransactionHistory] Adding ${result.transactions.length} new transactions to storage`);
          
          // Temporarily replace console.log
          const originalLog = console.log;
          console.log = () => {};
          
          // Add transactions to storage
          result.transactions.forEach(tx => {
            addTransaction(tx);
          });
          
          // Restore console.log
          console.log = originalLog;
        }
      } catch (err) {
        console.error('Error loading initial transactions:', err);
        setError(err.message || 'Failed to load transactions');
        
        // Fall back to local transactions on error
        const localTxs = getTransactions({
          address: address,
          network: currentChain.key
        });
        
        setTransactions(localTxs);
        setHasMore(false);
        setNextToken(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadInitialTransactions();
  }, [address, currentChain, addTransaction, getTransactions, limit, transactions.length]);
  
  // Function to refresh transactions
  const refreshTransactions = useCallback(async () => {
    if (!address || !currentChain || isRefreshing) return;
    
    setIsRefreshing(true);
    setError(null);
    
    try {
      console.log(`Refreshing transactions for ${address} on ${currentChain.key}`);
      
      // Reset pagination
      setNextToken(null);
      
      // Fetch transactions directly from API
      const result = await fetchTransactions(
        currentChain.key,
        address,
        { limit }
      );
      
      // Update state with fetched transactions
      setTransactions(result.transactions);
      setHasMore(result.hasMore);
      setNextToken(result.nextToken);
      
      console.log(`Refresh complete. Got ${result.transactions.length} transactions, hasMore: ${result.hasMore}`);
      
      // Silently add to storage
      if (result.transactions.length > 0) {
        console.log(`[useTransactionHistory] Adding ${result.transactions.length} refreshed transactions to storage`);
        
        // Temporarily replace console.log
        const originalLog = console.log;
        console.log = () => {};
        
        // Add transactions to storage
        result.transactions.forEach(tx => {
          addTransaction(tx);
        });
        
        // Restore console.log
        console.log = originalLog;
      }
    } catch (err) {
      console.error('Error refreshing transactions:', err);
      setError(err.message || 'Failed to refresh transactions');
    } finally {
      setIsRefreshing(false);
    }
  }, [address, currentChain, isRefreshing, addTransaction, limit]);
  
  // Function to load more transactions
  const loadMoreTransactions = useCallback(async () => {
    if (!hasMore || isLoading || isRefreshing || isLoadingMore || !nextToken || !address || !currentChain) {
      console.log('Cannot load more:', { 
        hasMore, isLoading, isRefreshing, isLoadingMore, hasNextToken: !!nextToken 
      });
      return;
    }
    
    setIsLoadingMore(true);
    
    try {
      console.log(`Loading more transactions with token: ${nextToken.substring(0, 20)}...`);
      
      // Fetch more transactions directly
      const result = await fetchTransactions(
        currentChain.key,
        address,
        { limit, nextToken }
      );
      
      console.log(`Load more returned ${result.transactions.length} transactions, hasMore: ${result.hasMore}`);
      
      if (result.transactions.length > 0) {
        // Filter out duplicates
        const existingHashes = new Set(transactions.map(tx => tx.hash));
        const uniqueNewTxs = result.transactions.filter(tx => !existingHashes.has(tx.hash));
        
        console.log(`Found ${uniqueNewTxs.length} unique new transactions to add`);
        
        if (uniqueNewTxs.length > 0) {
          // Update state by appending new transactions
          setTransactions(currentTxs => [...currentTxs, ...uniqueNewTxs]);
          
          // Silently add to storage
          const originalLog = console.log;
          console.log = () => {};
          uniqueNewTxs.forEach(tx => addTransaction(tx));
          console.log = originalLog;
        }
      }
      
      // Update pagination info
      setHasMore(result.hasMore);
      setNextToken(result.nextToken);
      
    } catch (err) {
      console.error('Error loading more transactions:', err);
      setError(err.message || 'Failed to load more transactions');
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasMore, isLoading, isRefreshing, isLoadingMore, nextToken,
    address, currentChain, transactions, addTransaction, limit
  ]);
  
  return {
    transactions,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    hasMore,
    refreshTransactions,
    loadMoreTransactions
  };
};

export default useTransactionHistory;